import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView, AnimatePresence, useScroll, useTransform, useSpring, useMotionValueEvent, type MotionValue } from 'framer-motion';
import { Send, ArrowRight, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DottedLogo } from './DottedLogo';
import { useCopy } from './copy/copyMode';
export function LivesInSlack({
  progress,
  hero = false
}: {
  progress?: MotionValue<number>;
  // When true, this Slack scene leads the page as the hero — it clears the
  // fixed nav and swaps its eyebrow/headline to the hero narrative.
  hero?: boolean;
}) {
  const copy = useCopy();
  const containerRef = useRef<HTMLDivElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, {
    margin: '-20%',
    once: false
  });
  const internalScroll = useScroll({
    target: containerRef,
    offset: ['start end', 'end start']
  });
  const scrollYProgress = progress || internalScroll.scrollYProgress;
  const [isActive, setIsActive] = useState(false);
  useMotionValueEvent(scrollYProgress, 'change', latest => {
    if (progress) {
      // Kick the conversation off as soon as the section comes into play.
      setIsActive(latest > 0.02);
    }
  });
  useEffect(() => {
    if (!progress) {
      setIsActive(isInView);
    }
  }, [isInView, progress]);
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mediaQuery.matches);
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);
  // Composer interaction — as the section scrolls into view, it plays out a
  // native Slack mention flow: someone types "@gi", a member-picker dropdown
  // opens, Giga is selected, the blue "@Giga" mention drops in, the rest of
  // the action-forward request is typed, then the message is sent.
  const MENTION_TRIGGER = '@gi';
  const REST_MSG = ' pull the Northwind account and draft a renewal brief for the 2pm call';
  type ComposerPhase = 'idle' | 'mention' | 'dropdown' | 'rest' | 'done' | 'sent' | 'thinking' | 'response';
  const [phase, setPhase] = useState<ComposerPhase>('idle');
  const [trigger, setTrigger] = useState('');
  const [rest, setRest] = useState('');
  const typingDone = phase === 'done' || phase === 'sent' || phase === 'thinking' || phase === 'response';
  // Once the message is sent, scroll the thread to the latest message so Giga's
  // reply (and brief) isn't buried beneath the composer.
  // Scroll ONLY the internal chat thread (never the page) so the animation
  // can't yank the whole window back to this section.
  const scrollThreadToEnd = () => {
    const el = threadRef.current;
    if (!el) return;
    el.scrollTo({
      top: el.scrollHeight,
      behavior: reducedMotion ? 'auto' : 'smooth'
    });
  };
  useEffect(() => {
    if (phase === 'sent' || phase === 'thinking' || phase === 'response') {
      scrollThreadToEnd();
      // Re-scroll several times as the response text, its tool chips, and the
      // card's spring scale all settle — otherwise the reply gets clipped
      // mid-sentence because the thread height keeps growing after the first
      // scroll fires. Staggered passes guarantee the full reply lands in view.
      const delays = [120, 350, 650, 1000, 1500];
      const timers = delays.map(d => setTimeout(scrollThreadToEnd, d));
      return () => timers.forEach(clearTimeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, reducedMotion]);
  useEffect(() => {
    if (!isActive) return;
    if (reducedMotion) {
      setTrigger(MENTION_TRIGGER);
      setRest(REST_MSG);
      setPhase('response');
      return;
    }
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    // Priya's @Giga message is shown already sent — no composer typing.
    // We jump straight to the posted message, then play Giga's reply.
    setTrigger(MENTION_TRIGGER);
    setRest(REST_MSG);
    setPhase('sent');
    // Giga is thinking — kept brief so the user gets to the actual result
    // (the drafted brief + sources) almost immediately.
    timeouts.push(setTimeout(() => {
      setPhase('thinking');
      // Giga responds
      timeouts.push(setTimeout(() => {
        setPhase('response');
      }, 450));
    }, 200));
    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [isActive, reducedMotion]);
  // Scroll-linked scale: the chat card starts small and grows as the section
  // travels up through the viewport, shrinking back when scrolling up.
  // We bound the max scale so it doesn't overwhelm the screen and
  // push the header out of view, while still achieving a slight bottom crop.
  const rawScale = useTransform(scrollYProgress, [0, 0.12, 1], [0.92, 1.0, 1.05]);
  const chatScale = useSpring(rawScale, {
    stiffness: 120,
    damping: 24,
    mass: 0.4
  });
  return <section className={`relative w-full overflow-hidden bg-[#FAF8F4] text-[#2B211A] ${progress ? 'h-screen flex flex-col justify-start pt-12 md:pt-28' : hero ? 'h-full pt-32 md:pt-44 pb-8 md:pb-16' : 'h-full pt-12 md:pt-32 pb-8 md:pb-16'}`} data-id="element-1334">
      {/* Soft, flowy gradient wash */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(120% 80% at 50% 0%, rgba(249,115,22,0.08) 0%, rgba(250,248,244,0) 55%), radial-gradient(90% 70% at 15% 90%, rgba(5,150,105,0.05) 0%, rgba(250,248,244,0) 60%)'
    }} data-id="element-1335" />

      {/* Dotted Giga logo background accent */}
      <DottedLogo variant="light" opacity={0.6} className="top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] md:w-[1200px] md:h-[1200px]" data-id="element-1336" />

      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-6 md:px-10 flex flex-col h-full" data-id="element-1337">
        {hero ? (
      /* HERO header — reads like a proper website hero: badge, h1,
      value-prop subcopy, and primary/secondary CTAs. */
      <div className="text-center mb-10 md:mb-14 shrink-0" data-id="element-1338">
            <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="inline-flex items-center gap-2 rounded-full border border-[#EA580C]/25 bg-[#EA580C]/[0.07] px-4 py-1.5 mb-6 md:mb-8" data-id="element-1339">
              <span className="flex gap-0.5 text-[#EA580C]" data-id="element-1340">
                {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-3 h-3" fill="currentColor" strokeWidth={0} data-id="element-1341" />)}
              </span>
              <span className="text-xs font-semibold text-[#9A3412]" data-id="element-1342">
                Loved by 1,200+ teams
              </span>
            </motion.div>

            <motion.h1 initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.05
        }} className="font-title font-medium tracking-tight leading-[0.98] text-[#2B211A] text-4xl sm:text-6xl md:text-[80px] max-w-5xl mx-auto mb-5 md:mb-7" data-id="element-1343">
              {copy.howItWorksConnections.headline1}
            </motion.h1>

            <motion.p initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.12
        }} className="text-lg md:text-xl text-neutral-500 max-w-2xl mx-auto leading-relaxed" data-id="element-1344">
              Giga is the AI coworker that works inside your tools — describe
              the outcome and it does the work, then hands back the finished
              result.
            </motion.p>

            <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="mt-8 md:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4" data-id="element-1345">
              <Link to="/signup" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#EA580C] hover:bg-[#C2410C] text-white px-8 py-3.5 rounded-full font-bold text-base md:text-lg transition-all duration-300 shadow-lg shadow-[#EA580C]/20 hover:shadow-xl hover:shadow-[#EA580C]/30 hover:-translate-y-0.5" data-id="element-1346">
                Try Giga for free
                <ArrowRight size={18} strokeWidth={2.5} data-id="element-1347" />
              </Link>
              <Link to="/demo/book" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white hover:bg-neutral-50 text-[#2B211A] border border-neutral-300 px-8 py-3.5 rounded-full font-bold text-base md:text-lg transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-0.5" data-id="element-1348">
                Book a demo
              </Link>
            </motion.div>
          </div>) : <div className="text-center mb-6 md:mb-8 shrink-0" data-id="element-1349">
            <span className="text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-3 md:mb-4 block" data-id="element-1350">
              Works where you work
            </span>
            <h2 className="text-3xl md:text-5xl font-title font-medium tracking-tight leading-[1.1] text-[#2B211A] max-w-5xl mx-auto mb-3 md:mb-4" data-id="element-1351">
              {copy.livesInSlack.headline}
            </h2>
            <p className="text-base md:text-lg text-neutral-500 max-w-2xl mx-auto leading-relaxed" data-id="element-1352">
              Add Giga to any channel and it answers right there — pulling from
              every connected tool, with the whole team in the loop.
            </p>
          </div>}

        {/* DESKTOP SCENE (md and up) */}
        <div ref={containerRef} className="relative z-10 mx-auto w-full max-w-[1040px] h-[60vh] min-h-[500px] max-h-[680px] hidden md:block mt-auto" data-id="element-1353">
          {/* Center chat box — anchored to the TOP of the scene so the card
           only ever grows DOWNWARD as it scales. This guarantees it never
           rises up over the eyebrow/headline/subcopy, and its bottom edge
           gets gently cropped by the section overflow at the end. */}
          <div className="absolute inset-0 pointer-events-none flex items-start justify-center z-20 pt-2" data-id="element-1354">
            {/* Scroll-scaled wrapper: grows the halo + chat together on scroll */}
            <motion.div style={{
            scale: reducedMotion ? 1 : chatScale,
            // Top origin so all growth extends downward, cropping at the
            // bottom of the section rather than overlapping the header.
            transformOrigin: 'center top'
          }} className="relative flex items-start justify-center" data-id="element-1355">
              {/* Chat Card — always present. It enters at the small end of the
               scroll-driven scale and grows/locks in as the section scrolls,
               so it's never invisible (no opacity-gated entrance that made it
               look like nothing was there). */}
              <motion.div style={{
              boxShadow: '0 24px 70px -15px rgba(0,0,0,0.18)',
              borderColor: 'rgba(229, 231, 235, 1)'
            }} className="relative pointer-events-auto w-[720px] bg-white rounded-2xl overflow-hidden border" data-id="element-1356">
                <div className="flex items-center justify-between px-4 py-3 bg-neutral-50 border-b border-neutral-100" data-id="element-1357">
                  <div className="flex gap-1.5" data-id="element-1358">
                    <div className="w-3 h-3 rounded-full bg-neutral-300" data-id="element-1359" />
                    <div className="w-3 h-3 rounded-full bg-neutral-300" data-id="element-1360" />
                    <div className="w-3 h-3 rounded-full bg-neutral-300" data-id="element-1361" />
                  </div>
                  <div className="flex items-center gap-1.5 text-neutral-600 text-sm font-medium" data-id="element-1362">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1363">
                      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" fill="#E01E5A" data-id="element-1364" />
                      <path d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.527 2.527 0 0 1 2.521 2.521 2.527 2.527 0 0 1-2.521 2.521H2.522A2.527 2.527 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z" fill="#36C5F0" data-id="element-1365" />
                      <path d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522v-2.521zm-1.272 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.161 0a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#2EB67D" data-id="element-1366" />
                      <path d="M15.161 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.161 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.272a2.527 2.527 0 0 1-2.52-2.521 2.526 2.526 0 0 1 2.52-2.522h6.317A2.527 2.527 0 0 1 24 15.163a2.528 2.528 0 0 1-2.522 2.521h-6.317z" fill="#ECB22E" data-id="element-1367" />
                    </svg>
                    Slack
                  </div>
                  <div className="w-8" data-id="element-1368" />
                </div>

                <div className="flex h-[580px]" data-id="element-1369">
                  <div className="w-40 shrink-0 bg-[#F6F5F3] text-neutral-600 border-r border-neutral-200/70 flex flex-col py-4" data-id="element-1370">
                    <div className="px-4 font-bold text-[#2B211A] mb-6 flex items-center gap-2" data-id="element-1371">
                      <div className="w-6 h-6 rounded bg-[#2B211A] text-white flex items-center justify-center font-bold text-xs shrink-0" data-id="element-1372">
                        A
                      </div>
                      <span className="truncate" data-id="element-1373">Acme</span>
                    </div>
                    <div className="px-4 text-xs font-bold mb-2 text-neutral-400 uppercase tracking-wider" data-id="element-1374">
                      Channels
                    </div>
                    <div className="flex flex-col text-[13px] font-medium" data-id="element-1375">
                      <div className="px-4 py-1.5 hover:bg-black/[0.04] cursor-pointer transition-colors" data-id="element-1376">
                        # general
                      </div>
                      <div className="px-4 py-1.5 hover:bg-black/[0.04] cursor-pointer transition-colors" data-id="element-1377">
                        # sales
                      </div>
                      <div className="px-4 py-1.5 hover:bg-black/[0.04] cursor-pointer transition-colors" data-id="element-1378">
                        # engineering
                      </div>
                      <div className="px-4 py-1.5 bg-[#2B211A]/[0.06] text-[#2B211A] font-bold cursor-pointer" data-id="element-1379">
                        # northwind
                      </div>
                      <div className="px-4 py-1.5 hover:bg-black/[0.04] cursor-pointer transition-colors" data-id="element-1380">
                        # marketing
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 flex flex-col bg-white" data-id="element-1381">
                    <div className="px-5 py-3.5 border-b border-neutral-100 flex items-center justify-between" data-id="element-1382">
                      <div className="flex items-center gap-2" data-id="element-1383">
                        <div className="w-2.5 h-2.5 rounded-full bg-[#F97316]" data-id="element-1384" />
                        <span className="font-bold text-[#2B211A] text-sm sm:text-base" data-id="element-1385">
                          Giga
                        </span>
                        <span className="text-[10px] sm:text-xs font-bold bg-[#FFEDD5] text-[#EA580C] px-1.5 py-0.5 rounded" data-id="element-1386">
                          APP
                        </span>
                      </div>
                      <span className="text-xs sm:text-sm text-neutral-400" data-id="element-1387">
                        Acme
                      </span>
                    </div>

                    <div ref={threadRef} className="flex-1 p-5 overflow-y-auto relative scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-1388">
                      <div className="flex flex-col gap-5 sm:gap-6" data-id="element-1389">
                        {/* Sent Message */}
                        <AnimatePresence data-id="element-1390">
                          {(phase === 'sent' || phase === 'thinking' || phase === 'response') && <motion.div initial={{
                          opacity: 0,
                          y: 10
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} className="flex gap-3 sm:gap-4" data-id="element-1391">
                              <img src="https://cdn.magicpatterns.com/uploads/74dN9xBfAH2PfDiwLgxxoL/CleanShot_2026-05-20_at_21.29.242x.png" alt="Faith" className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg object-cover shrink-0" data-id="element-1392" />
                              <div className="min-w-0 flex-1" data-id="element-1393">
                                <div className="flex items-baseline gap-2 mb-1" data-id="element-1394">
                                  <span className="font-bold text-[#333333] text-sm sm:text-base" data-id="element-1395">
                                    Faith
                                  </span>
                                  <span className="text-xs text-neutral-400" data-id="element-1396">
                                    1:50 PM
                                  </span>
                                </div>
                                <p className="text-neutral-700 text-sm sm:text-base leading-relaxed" data-id="element-1397">
                                  <span className="text-[#1264A3] bg-[#1264A3]/10 rounded px-1 py-0.5 font-medium mr-1" data-id="element-1398">
                                    @Giga
                                  </span>
                                  {REST_MSG}
                                </p>
                              </div>
                            </motion.div>}
                        </AnimatePresence>

                        {/* Thinking / Response */}
                        <AnimatePresence mode="wait" data-id="element-1399">
                          {phase === 'thinking' && <motion.div key="thinking" initial={{
                          opacity: 0,
                          y: 10
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} exit={{
                          opacity: 0,
                          y: -10
                        }} className="flex gap-3 sm:gap-4" data-id="element-1400">
                              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#EA580C] flex items-center justify-center shrink-0" data-id="element-1401">
                                <img src="https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png" alt="Giga" className="w-5 h-5 sm:w-6 sm:h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-1402" />
                              </div>
                              <div className="min-w-0 flex-1" data-id="element-1403">
                                <div className="flex items-baseline gap-2 mb-1" data-id="element-1404">
                                  <span className="font-bold text-[#333333] text-sm sm:text-base" data-id="element-1405">
                                    Giga
                                  </span>
                                  <span className="text-[10px] sm:text-xs font-bold bg-[#FFEDD5] text-[#EA580C] px-1.5 py-0.5 rounded" data-id="element-1406">
                                    APP
                                  </span>
                                  <span className="text-xs text-neutral-400" data-id="element-1407">
                                    1:50 PM
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5 mt-2" data-id="element-1408">
                                  <motion.div className="w-1.5 h-1.5 bg-neutral-400 rounded-full" animate={{
                                y: [0, -3, 0]
                              }} transition={{
                                duration: 0.6,
                                repeat: Infinity,
                                delay: 0
                              }} data-id="element-1409" />
                                  <motion.div className="w-1.5 h-1.5 bg-neutral-400 rounded-full" animate={{
                                y: [0, -3, 0]
                              }} transition={{
                                duration: 0.6,
                                repeat: Infinity,
                                delay: 0.2
                              }} data-id="element-1410" />
                                  <motion.div className="w-1.5 h-1.5 bg-neutral-400 rounded-full" animate={{
                                y: [0, -3, 0]
                              }} transition={{
                                duration: 0.6,
                                repeat: Infinity,
                                delay: 0.4
                              }} data-id="element-1411" />
                                  <span className="text-xs text-neutral-500 ml-1" data-id="element-1412">
                                    Pulling context from your stack...
                                  </span>
                                </div>
                              </div>
                            </motion.div>}
                          {phase === 'response' && <motion.div key="response" initial={{
                          opacity: 0,
                          y: 10
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} onAnimationComplete={scrollThreadToEnd} className="flex gap-3 sm:gap-4" data-id="element-1413">
                              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#EA580C] flex items-center justify-center shrink-0" data-id="element-1414">
                                <img src="https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png" alt="Giga" className="w-5 h-5 sm:w-6 sm:h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-1415" />
                              </div>
                              <div className="min-w-0 flex-1" data-id="element-1416">
                                <div className="flex items-baseline gap-2 mb-1" data-id="element-1417">
                                  <span className="font-bold text-[#333333] text-sm sm:text-base" data-id="element-1418">
                                    Giga
                                  </span>
                                  <span className="text-[10px] sm:text-xs font-bold bg-[#FFEDD5] text-[#EA580C] px-1.5 py-0.5 rounded" data-id="element-1419">
                                    APP
                                  </span>
                                  <span className="text-xs text-neutral-400" data-id="element-1420">
                                    1:50 PM
                                  </span>
                                </div>

                                <div className="mt-1" data-id="element-1421">
                                  <motion.div initial={{
                                opacity: 0
                              }} animate={{
                                opacity: 1
                              }} transition={{
                                delay: 0.1,
                                duration: 0.4
                              }} className="text-sm sm:text-[15px] text-neutral-800 leading-relaxed mb-3" data-id="element-1422">
                                    I've pulled the Northwind account context
                                    and drafted the renewal brief in Notion.
                                    It's attached to your 2pm call notes, with
                                    the open API risk{' '}
                                    <span className="font-bold" data-id="element-1423">(ENG-402)</span>{' '}
                                    flagged so you can lead with the extension
                                    offer.
                                  </motion.div>

                                  <div className="flex flex-col gap-1.5 pl-3 border-l-[3px] border-[#EA580C]/80" data-id="element-1424">
                                    {[{
                                  name: 'HubSpot',
                                  icon: 'hubspot',
                                  text: 'Pull Northwind account history and renewal date'
                                }, {
                                  name: 'Gmail',
                                  icon: 'gmail',
                                  text: 'Scan recent email threads with Northwind stakeholders'
                                }, {
                                  name: 'Slack',
                                  icon: 'slack',
                                  text: 'Search #acct-northwind for recent context'
                                }, {
                                  name: 'Google Calendar',
                                  icon: 'googlecalendar',
                                  text: 'Check attendees for the 2pm call'
                                }, {
                                  name: 'Notion',
                                  icon: 'notion',
                                  text: 'Draft renewal brief in the Q3 Renewals workspace'
                                }].map((tool, i) => <motion.div key={tool.name} initial={{
                                  opacity: 0,
                                  x: -5
                                }} animate={{
                                  opacity: 1,
                                  x: 0
                                }} transition={{
                                  delay: 0.6 + i * 0.12,
                                  duration: 0.3
                                }} className="flex items-center gap-2 text-[13px] sm:text-sm text-neutral-600" data-id="element-1425">
                                        <img src={`https://cdn.simpleicons.org/${tool.icon}/737373`} className="w-3.5 h-3.5" alt="" data-id="element-1426" />
                                        <span className="truncate" data-id="element-1427">
                                          <strong className="text-neutral-800 font-semibold" data-id="element-1428">
                                            {tool.name}
                                          </strong>{' '}
                                          {tool.text}
                                        </span>
                                      </motion.div>)}
                                  </div>
                                </div>
                              </div>
                            </motion.div>}
                        </AnimatePresence>

                        {/* Scroll anchor — keeps Giga's reply in view on send */}
                        <div ref={messagesEndRef} className="h-0" data-id="element-1429" />
                      </div>
                    </div>

                    <div className="relative p-4 sm:p-5" data-id="element-1430">
                      {/* Slack-style member-picker dropdown */}
                      <AnimatePresence data-id="element-1431">
                        {phase === 'dropdown' && <motion.div key="mention-dropdown" initial={{
                        opacity: 0,
                        y: 8,
                        scale: 0.98
                      }} animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1
                      }} exit={{
                        opacity: 0,
                        y: 8,
                        scale: 0.98
                      }} transition={{
                        duration: 0.18,
                        ease: 'easeOut'
                      }} className="absolute left-4 right-4 sm:left-5 sm:right-5 bottom-[calc(100%-0.5rem)] z-30 bg-white rounded-xl border border-neutral-200 shadow-xl overflow-hidden" data-id="element-1432">
                            <div className="px-3 pt-2.5 pb-1.5 text-[10px] sm:text-xs font-bold uppercase tracking-wider text-neutral-400" data-id="element-1433">
                              Members
                            </div>
                            {/* Highlighted Giga row */}
                            <div className="flex items-center gap-2.5 px-3 py-2 bg-[#2B211A]/10" data-id="element-1434">
                              <div className="w-7 h-7 rounded-md bg-[#EA580C] flex items-center justify-center shrink-0" data-id="element-1435">
                                <img src="https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png" alt="" className="w-4 h-4 object-contain [filter:brightness(0)_invert(1)]" data-id="element-1436" />
                              </div>
                              <div className="flex items-center gap-1.5 min-w-0" data-id="element-1437">
                                <span className="font-bold text-[#2B211A] text-sm truncate" data-id="element-1438">
                                  Giga
                                </span>
                                <span className="text-[9px] sm:text-[10px] font-bold bg-[#FFEDD5] text-[#EA580C] px-1 py-0.5 rounded" data-id="element-1439">
                                  APP
                                </span>
                              </div>
                              <span className="ml-auto text-xs text-neutral-400 hidden sm:block" data-id="element-1440">
                                works across your tools
                              </span>
                            </div>
                            {/* Dimmed member for realism */}
                            <div className="flex items-center gap-2.5 px-3 py-2" data-id="element-1441">
                              <div className="w-7 h-7 rounded-md bg-neutral-200 text-neutral-500 flex items-center justify-center font-bold text-xs shrink-0" data-id="element-1442">
                                GC
                              </div>
                              <span className="text-sm text-neutral-500 truncate" data-id="element-1443">
                                Gianna Cole
                              </span>
                            </div>
                          </motion.div>}
                      </AnimatePresence>

                      <div className="bg-neutral-50 border border-neutral-200 rounded-2xl px-5 py-3 sm:py-3.5 flex items-start gap-2 shadow-sm cursor-text hover:border-neutral-300 transition-colors" data-id="element-1444">
                        <span className="text-sm sm:text-base flex-1 min-w-0" data-id="element-1445">
                          {phase === 'idle' || phase === 'sent' || phase === 'thinking' || phase === 'response' ? <span className="text-neutral-400" data-id="element-1446">
                              Message Giga
                            </span> : <span className="text-neutral-800 break-words" data-id="element-1447">
                              {phase === 'mention' || phase === 'dropdown' ? trigger : <>
                                  <span className="text-[#1264A3] bg-[#1264A3]/10 rounded px-1 py-0.5 font-medium" data-id="element-1448">
                                    @Giga
                                  </span>
                                  {rest}
                                </>}
                              {!reducedMotion && phase !== 'done' && phase !== 'dropdown' && <motion.span aria-hidden="true" className="inline-block w-[2px] h-4 align-middle bg-[#EA580C] ml-0.5" animate={{
                            opacity: [1, 0]
                          }} transition={{
                            duration: 0.6,
                            repeat: Infinity
                          }} data-id="element-1449" />}
                            </span>}
                        </span>
                        <motion.div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center shrink-0 cursor-pointer" animate={typingDone ? {
                        backgroundColor: ['#2B211A', '#EA580C', '#2B211A'],
                        scale: [1, 0.82, 1.05, 1]
                      } : {
                        backgroundColor: '#2B211A',
                        scale: 1
                      }} transition={{
                        duration: 0.5,
                        ease: 'easeOut'
                      }} data-id="element-1450">
                          <Send size={14} className="text-white -ml-0.5 sm:w-4 sm:h-4" data-id="element-1451" />
                        </motion.div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* MOBILE SCENE (below md) */}
        <div className="md:hidden mt-8 w-full" data-id="element-1452">
          <div className="w-full bg-white rounded-2xl overflow-hidden border border-neutral-200 shadow-xl" data-id="element-1453">
            {/* Slack Chrome Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-neutral-50 border-b border-neutral-100" data-id="element-1454">
              <div className="flex gap-1.5" data-id="element-1455">
                <div className="w-3 h-3 rounded-full bg-neutral-300" data-id="element-1456" />
                <div className="w-3 h-3 rounded-full bg-neutral-300" data-id="element-1457" />
                <div className="w-3 h-3 rounded-full bg-neutral-300" data-id="element-1458" />
              </div>
              <div className="flex items-center gap-1.5 text-neutral-600 text-sm font-medium" data-id="element-1459">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1460">
                  <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" fill="#E01E5A" data-id="element-1461" />
                  <path d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.527 2.527 0 0 1 2.521 2.521 2.527 2.527 0 0 1-2.521 2.521H2.522A2.527 2.527 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z" fill="#36C5F0" data-id="element-1462" />
                  <path d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522v-2.521zm-1.272 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.161 0a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#2EB67D" data-id="element-1463" />
                  <path d="M15.161 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.161 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.272a2.527 2.527 0 0 1-2.52-2.521 2.526 2.526 0 0 1 2.52-2.522h6.317A2.527 2.527 0 0 1 24 15.163a2.528 2.528 0 0 1-2.522 2.521h-6.317z" fill="#ECB22E" data-id="element-1464" />
                </svg>
                Slack
              </div>
              <div className="w-8" data-id="element-1465" />
            </div>

            {/* Channel Header */}
            <div className="px-4 py-3 border-b border-neutral-100 flex items-center justify-between" data-id="element-1466">
              <div className="flex items-center gap-2" data-id="element-1467">
                <div className="w-2.5 h-2.5 rounded-full bg-[#F97316]" data-id="element-1468" />
                <span className="font-bold text-[#2B211A] text-sm" data-id="element-1469">Giga</span>
                <span className="text-[10px] font-bold bg-[#FFEDD5] text-[#EA580C] px-1.5 py-0.5 rounded" data-id="element-1470">
                  APP
                </span>
              </div>
              <span className="text-xs text-neutral-400" data-id="element-1471">Acme</span>
            </div>

            {/* Mobile Thread — Staged Reveal */}
            <div className="p-4 flex flex-col gap-6" data-id="element-1472">
              {/* Message (Priya mentions Giga) — preview starts here */}
              <motion.div initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-10%'
            }} transition={{
              duration: 0.5,
              delay: reducedMotion ? 0 : 0.2
            }} className="flex gap-3" data-id="element-1473">
                <img src="https://cdn.magicpatterns.com/uploads/74dN9xBfAH2PfDiwLgxxoL/CleanShot_2026-05-20_at_21.29.242x.png" alt="Faith" className="w-9 h-9 rounded-lg object-cover shrink-0" data-id="element-1474" />
                <div className="min-w-0 flex-1" data-id="element-1475">
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-1476">
                    <span className="font-bold text-[#333333] text-sm" data-id="element-1477">
                      Faith
                    </span>
                    <span className="text-xs text-neutral-400" data-id="element-1478">1:50 PM</span>
                  </div>
                  <p className="text-neutral-700 text-sm leading-relaxed" data-id="element-1479">
                    <span className="text-[#1264A3] bg-[#1264A3]/10 rounded px-1 py-0.5 font-medium mr-1" data-id="element-1480">
                      @Giga
                    </span>
                    {REST_MSG}
                  </p>
                </div>
              </motion.div>

              {/* Giga replies */}
              <motion.div initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-10%'
            }} transition={{
              duration: 0.5,
              delay: reducedMotion ? 0 : 1.0
            }} className="flex gap-3" data-id="element-1481">
                <div className="w-9 h-9 rounded-lg bg-[#EA580C] flex items-center justify-center shrink-0 shadow-sm" data-id="element-1482">
                  <img src="https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png" alt="Giga" className="w-6 h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-1483" />
                </div>
                <div className="min-w-0 flex-1" data-id="element-1484">
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-1485">
                    <span className="font-bold text-[#333333] text-sm" data-id="element-1486">
                      Giga
                    </span>
                    <span className="text-[10px] font-bold bg-[#FFEDD5] text-[#EA580C] px-1.5 py-0.5 rounded" data-id="element-1487">
                      APP
                    </span>
                    <span className="text-xs text-neutral-400" data-id="element-1488">1:50 PM</span>
                  </div>

                  <div className="text-sm text-neutral-800 leading-relaxed mt-1" data-id="element-1489">
                    On it — pulled the Northwind account from HubSpot and
                    drafted the renewal brief in Notion. It's attached to your
                    2pm call notes, with the open API risk{' '}
                    <span className="font-semibold text-[#2B211A]" data-id="element-1490">
                      (ENG-402)
                    </span>{' '}
                    flagged so you can lead with the extension offer.
                    <div className="flex items-center gap-2 mt-3" data-id="element-1491">
                      <div className="inline-flex items-center gap-1.5 bg-white border border-neutral-200 rounded-full px-2 py-1 shadow-sm" data-id="element-1492">
                        <img src="https://cdn.simpleicons.org/hubspot" alt="" className="w-3 h-3" data-id="element-1493" />
                        <span className="text-[10px] font-bold text-neutral-600" data-id="element-1494">
                          HubSpot
                        </span>
                      </div>
                      <div className="inline-flex items-center gap-1.5 bg-white border border-neutral-200 rounded-full px-2 py-1 shadow-sm" data-id="element-1495">
                        <img src="https://cdn.simpleicons.org/notion" alt="" className="w-3 h-3" data-id="element-1496" />
                        <span className="text-[10px] font-bold text-neutral-600" data-id="element-1497">
                          Notion
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}